import { useEffect, useRef, useState } from 'react';
import { useScrollLock } from '../utils/scrollLock';
import './SignOutModal.css';

const FOCUSABLE = 'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

/**
 * "Log out?" confirmation. Rendered by CommunityAuthProvider; open it with
 * `openSignOut()` from useCommunityAuth(), don't mount it yourself.
 */
export default function SignOutModal({ name, onCancel, onConfirm }) {
  useScrollLock(); // shared lock: safe when popups overlap
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const sheetRef = useRef(null);
  const cancelRef = useRef(null);
  const busyRef = useRef(busy);
  busyRef.current = busy;

  // Focus "Cancel" (the safe choice), lock scroll, give focus back on close.
  useEffect(() => {
    const previous = document.activeElement;
    cancelRef.current?.focus();
    return () => {
      if (previous && previous.focus && document.contains(previous)) previous.focus();
    };
  }, []);

  useEffect(() => {
    const onEscape = (e) => {
      if (e.key === 'Escape' && !busyRef.current) onCancel();
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [onCancel]);

  const onKeyDown = (e) => {
    if (e.key !== 'Tab') return;
    const items = [...sheetRef.current.querySelectorAll(FOCUSABLE)];
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const confirm = async () => {
    setBusy(true);
    setError('');
    try {
      await onConfirm();
    } catch (err) {
      console.error('Sign out failed', err);
      setError('We couldn’t log you out. Check your connection and try again.');
      setBusy(false);
    }
  };

  return (
    <div
      className="signout-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div
        className="signout-sheet"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="signout-title"
        aria-describedby="signout-text"
        ref={sheetRef}
        onKeyDown={onKeyDown}
      >
        <span className="signout-icon" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <path d="M16 17l5-5-5-5" />
            <path d="M21 12H9" />
          </svg>
        </span>
        <h2 id="signout-title">{name ? `Log out, ${name}?` : 'Log out?'}</h2>
        <p id="signout-text" className="signout-text">
          You can still browse members’ portfolios. Sign back in any time to follow people and update your own.
        </p>

        {error && <p className="signout-error" role="alert">{error}</p>}

        <div className="signout-actions">
          <button type="button" className="signout-btn signout-btn--ghost" ref={cancelRef} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="signout-btn signout-btn--primary" onClick={confirm} disabled={busy}>
            {busy ? 'Logging out…' : 'Log out'}
          </button>
        </div>
      </div>
    </div>
  );
}
